import { useState } from 'react';
import Gift from 'lucide-react/dist/esm/icons/gift.js';
import Trophy from 'lucide-react/dist/esm/icons/trophy.js';
import Star from 'lucide-react/dist/esm/icons/star.js';
import ImageOff from 'lucide-react/dist/esm/icons/image-off.js';
import PrizeDetailModal from './PrizeDetailModal';
import { getLocalizedPrizeField } from '../utils/prizeHelpers';
import { useLanguage } from '../contexts/LanguageContext';

/**
 * PrizeGallery Component
 * Displays available prizes in a responsive grid for Prize Draw game type.
 * Accepts real prize data via props; provides sensible fallback structure.
 *
 * Expected prize shape:
 * { id, name, description, value, image_url, tier ('grand'|'first'|'second'|'third'|'consolation') }
 */

export const TIER_CONFIG = {
  grand: {
    label: 'Grand Prize',
    gradient: 'from-yellow-400 via-amber-500 to-orange-500',
    ring: 'ring-yellow-400/50',
    bg: 'bg-gradient-to-br from-yellow-50 to-amber-50 dark:from-yellow-900/20 dark:to-amber-900/20',
    border: 'border-yellow-300 dark:border-yellow-600/50',
    icon: Trophy,
    iconColor: 'text-yellow-600 dark:text-yellow-400',
  },
  first: {
    label: '1st Prize',
    gradient: 'from-purple-500 to-indigo-600',
    ring: 'ring-purple-400/50',
    bg: 'bg-gradient-to-br from-purple-50 to-indigo-50 dark:from-purple-900/20 dark:to-indigo-900/20',
    border: 'border-purple-300 dark:border-purple-600/50',
    icon: Star,
    iconColor: 'text-purple-600 dark:text-purple-400',
  },
  second: {
    label: '2nd Prize',
    gradient: 'from-blue-500 to-cyan-500',
    ring: 'ring-blue-400/50',
    bg: 'bg-white dark:bg-gray-800',
    border: 'border-blue-200 dark:border-blue-700/50',
    icon: Gift,
    iconColor: 'text-blue-600 dark:text-blue-400',
  },
  third: {
    label: '3rd Prize',
    gradient: 'from-teal-500 to-green-500',
    ring: 'ring-teal-400/50',
    bg: 'bg-white dark:bg-gray-800',
    border: 'border-teal-200 dark:border-teal-700/50',
    icon: Gift,
    iconColor: 'text-teal-600 dark:text-teal-400',
  },
  consolation: {
    label: 'Consolation',
    gradient: 'from-gray-400 to-gray-500',
    ring: 'ring-gray-300/50',
    bg: 'bg-white dark:bg-gray-800',
    border: 'border-gray-200 dark:border-gray-700',
    icon: Gift,
    iconColor: 'text-gray-500 dark:text-gray-400',
  },
};

function getTierConfig(tier) {
  return TIER_CONFIG[tier] || TIER_CONFIG.consolation;
}

function PrizeCard({ prize, featured = false, onClick, language }) {
  const tier = getTierConfig(prize.tier);
  const TierIcon = tier.icon;
  const name = getLocalizedPrizeField(prize.name, language);
  const description = getLocalizedPrizeField(prize.description, language);

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onClick?.(prize)}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick?.(prize); } }}
      className={`
        group relative rounded-2xl overflow-hidden border-2 transition-all duration-300 cursor-pointer
        ${tier.border} ${tier.bg}
        ${featured ? 'ring-2 ' + tier.ring + ' shadow-xl' : 'shadow-md hover:shadow-xl'}
        hover:-translate-y-1
      `}
    >
      {/* Tier Badge */}
      <div className={`absolute top-3 left-3 z-10 px-2.5 py-1 rounded-full bg-gradient-to-r ${tier.gradient} text-white text-xs font-bold uppercase tracking-wide shadow-lg`}>
        {tier.label}
      </div>

      {/* Prize Image */}
      <div className={`relative ${featured ? 'h-48 sm:h-56' : 'h-36 sm:h-44'} bg-gray-100 dark:bg-gray-700 overflow-hidden`}>
        {prize.image_url ? (
          <img
            src={prize.image_url}
            alt={name}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full flex flex-col items-center justify-center">
            <TierIcon className={`w-12 h-12 ${tier.iconColor} mb-2 opacity-40`} />
            <ImageOff className="w-5 h-5 text-gray-300 dark:text-gray-600" />
          </div>
        )}
        {/* Shimmer overlay on hover */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
      </div>

      {/* Prize Info */}
      <div className="p-4">
        <h4 className={`font-bold mb-1 ${featured ? 'text-lg' : 'text-base'} text-gray-900 dark:text-white`}>
          {name}
        </h4>
        {description && (
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-3 line-clamp-2">
            {description}
          </p>
        )}
        {prize.value != null && (
          <div className="flex items-center gap-1.5">
            <span className={`text-xs font-semibold uppercase tracking-wide ${tier.iconColor}`}>
              Value
            </span>
            <span className={`${featured ? 'text-xl' : 'text-lg'} font-black text-gray-900 dark:text-white`}>
              ${Number(prize.value).toLocaleString()}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

export default function PrizeGallery({
  prizes = [],
  title = 'Prize Gallery',
  className = '',
  columns = 'auto',
  onBuyTickets,
}) {
  const [selectedPrize, setSelectedPrize] = useState(null);
  const { language } = useLanguage();

  if (prizes.length === 0) return null;

  // Separate grand/featured prize from others
  const grandPrize = prizes.find(p => p.tier === 'grand');
  const otherPrizes = prizes.filter(p => p.tier !== 'grand');

  const gridCols = columns === 'auto'
    ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3'
    : `grid-cols-1 sm:grid-cols-${Math.min(columns, 2)} lg:grid-cols-${columns}`;

  return (
    <>
      <div className={`bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-700 overflow-hidden ${className}`}>
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-600 via-indigo-600 to-blue-600 px-5 py-4">
          <h3 className="text-lg font-bold text-white flex items-center gap-2">
            <Gift className="w-5 h-5" />
            {title}
          </h3>
          <p className="text-purple-200 text-sm mt-0.5">
            {prizes.length} prize{prizes.length !== 1 ? 's' : ''} available
          </p>
        </div>

        <div className="p-5 space-y-5">
          {/* Grand Prize — featured card, full width */}
          {grandPrize && (
            <PrizeCard prize={grandPrize} featured onClick={setSelectedPrize} language={language} />
          )}

          {/* Other Prizes Grid */}
          {otherPrizes.length > 0 && (
            <div className={`grid ${gridCols} gap-4`}>
              {otherPrizes.map((prize, index) => (
                <PrizeCard key={prize.id || index} prize={prize} onClick={setSelectedPrize} language={language} />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Prize Detail Modal */}
      <PrizeDetailModal
        prize={selectedPrize}
        isOpen={!!selectedPrize}
        onClose={() => setSelectedPrize(null)}
        onBuyTickets={onBuyTickets}
      />
    </>
  );
}
